<template>
	<div ref="treeView" v-loading="isAsyncOperationPending" class="component-groups-tree-container">
		<div style="margin-bottom: .6rem;">
			<el-input placeholder="Фильтр по названию" clearable v-model="filterGroupsTreeQuery" />
		</div>
		<div>
			<el-scrollbar class="component-groups-scrollbar">				
				<el-tree
					:loading="isAsyncOperationPending"
					ref="elTree"
					class="component-groups-tree"		
					show-checkbox
					check-strictly
					node-key="value"						
					:data="tree"
					:props="{ children: 'children', label: 'label' }"
					:filter-node-method="filterGrpupsTree"
					@check-change="onParentGroupTreeChange">
					<template #empty>
						&nbsp;
					</template>
				</el-tree>			
			</el-scrollbar>
		</div>
	</div>
	<el-checkbox v-if="!disableRoot" label="Корневая группа" :disabled="isAsyncOperationPending" v-model="isRootFolderSelected" @change="onChecboxRootGroupChange" />		
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { ElInput, ElNotification, ElScrollbar, ElTree } from 'element-plus';
import LinkedLabelValuePair from '@/models/LinkedLabelValuePair';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import { watch, WatchStopHandle } from 'vue';

@Options({	
	components: {
		ElInput, ElTree, ElScrollbar
	},
	props: {
		url: String,
		modelValue: Number,
		disableRoot: {
			type: Boolean,
			default: false
		}
	},
	emits: ['update:modelValue']
})
export default class TreeViewComponent extends Vue {
	/** props */
	url!: string;
	modelValue!: number;
	disableRoot!: boolean;

	/** constants */
	treeElement!: any;

	/** view-models */
	isAsyncOperationPending = true;
		
	filterGroupsTreeQuery = '';
	filterGroupsTreeQueryWatch: Function | null = null;
	
	isRootFolderSelected = false;
	selectedNode: number | null = null;
	previoslySelectedNode: number | null = null;
	tree: Array<LinkedLabelValuePair<string, number>> | null = null;

	modelValueWatchSubscription: WatchStopHandle | null = null;

	mounted(): void {
		this.treeElement = this.$refs.elTree;
		this.fetchGroupsTreeAsync()
			.then(() => {
				if (this.modelValue) {
					this.selectedNode = this.modelValue;
					this.treeElement.setChecked(this.modelValue, true);
				} else {
					if (!this.disableRoot) {
						this.isRootFolderSelected = true;
						this.onChecboxRootGroupChange(this.isRootFolderSelected);
					}
				}
			});

		this.filterGroupsTreeQueryWatch = watch(
			() => this.filterGroupsTreeQuery,
			(query: string) => this.treeElement.filter(query)
		);

		this.modelValueWatchSubscription = watch(
			() => this.modelValue,
			(v) => {
				if (v) {
					this.selectedNode = v;
					this.treeElement.setChecked(v, true);
				} else {
					const checkedKeys = this.treeElement.getCheckedKeys() as Array<number>;
					checkedKeys.forEach(key => this.treeElement.setChecked(key, false));
				}
			});
	}

	unmounted(): void {
		if (this.filterGroupsTreeQueryWatch) {
			this.filterGroupsTreeQueryWatch();
		}

		if (this.modelValueWatchSubscription) {
			this.modelValueWatchSubscription();
		}
	}

	onChecboxRootGroupChange(isRootGroup: boolean) {	
		this.toggleOverlay();	
		if(!isRootGroup) {
			if (this.previoslySelectedNode) {
				this.selectedNode = this.previoslySelectedNode!;
			}			
		} else {			
			if (this.selectedNode) {
				this.treeElement.setChecked(this.selectedNode, false);
				this.previoslySelectedNode = this.selectedNode;
				this.selectedNode = null;
			}			
		}

		this.$emit('update:modelValue', this.selectedNode);
	}

	onParentGroupTreeChange(node: LinkedLabelValuePair<string, number>, checked: boolean) {
		if (!checked) {
			this.selectedNode = null;
			this.$emit('update:modelValue', this.selectedNode);
		}
		else {			
			const checkedNode = node.value;			
			const checkedNodes = this.treeElement.getCheckedKeys() as Array<number>;
			
			checkedNodes.forEach(nodeId => {
				if (nodeId != checkedNode) {
					this.treeElement.setChecked(nodeId, false);
				}
			});

			this
				.$nextTick()
				.then(() => {
					this.selectedNode = checkedNode;
					this.$emit('update:modelValue', this.selectedNode);
				});			
		}
	}

	filterGrpupsTree(value: string, data: LinkedLabelValuePair<string, number>) {
		if (!value) {
			return true;
		}

		return data.label.toLowerCase().includes(value.toLowerCase());
	}

	async fetchGroupsTreeAsync() {
		try {
			this.isAsyncOperationPending = true;
			const getTreeHttpClient = axiosHelper.createInstance(store.getters.jwt);
			const response = await getTreeHttpClient.get(this.url);
			if (!response?.data?.length) {
				this.tree = [];
				return;
			}
			const groupsTree = this.mapTree(response.data);		
			this.tree = groupsTree;
		}
		catch(e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить список`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузки списка`,
					type: 'error',
				});
			}
			console.error('Error loading list', e)
		} finally {
			this.isAsyncOperationPending = false;
		}
	}

	mapTree(data: Array<any>) : Array<LinkedLabelValuePair<string, number>> {
		const map = this.mapTree;
		return data.map((g: any) => {
			const group = new LinkedLabelValuePair<string, number>({				
				label: g.name,
				value: g.id
			});
			if (!g?.childGroups?.length) {
				group.children = undefined;
			} 
			else {
				group.children = map(g.childGroups);
			}
			
			return group;
		});
	}
	
	toggleOverlay() {
		const overlayClassName = 't-overlay';
		const treeViewContainerEl = this.$refs.treeView as Element;		
		const overlayEl = treeViewContainerEl.getElementsByClassName(overlayClassName);

		if (overlayEl?.length) {
			overlayEl[0].remove();
		} else {
			const treeViewContainerRect = treeViewContainerEl.getBoundingClientRect();
			const overlay = document.createElement('div');
			overlay.classList.add('t-overlay');
			overlay.style.width = treeViewContainerRect.width + 'px';
			overlay.style.height = treeViewContainerRect.height + 'px';
			treeViewContainerEl.prepend(overlay);
		}
	}
}
</script>

<style>
.component-groups-tree-container {
	width: 100%;
}

	.t-overlay {
		position: absolute;
		z-index: 2;
		opacity: .4;
		background: #fff;
		cursor: default;
		border: var(--el-border);
		border-width: 1px;
		border-color: transparent;
	}

	.component-groups-scrollbar {
		max-height: 280px; 
		height: 280px; 
		width: 100%; 
		border: var(--el-border);
	}

		.component-groups-tree {
			width: 100%;
		}
</style>